import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';

/**
 * Props for the ImageCropper component.
 */
interface ImageCropperProps {
    /** The image source URL (typically a data URL from FileReader) */
    imageSrc: string;
    /** Callback when cropping is complete, receives the cropped image as a Blob */
    onCropComplete: (croppedBlob: Blob) => void;
    /** Callback when the user cancels cropping */
    onCancel: () => void;
    /** Target width for the cropped image (default: 864) */
    targetWidth?: number;
    /** Target height for the cropped image (default: 384) */
    targetHeight?: number;
}

/**
 * Creates a cropped image from the source image and crop area.
 * Returns a Blob of the cropped image at the specified dimensions.
 */
async function getCroppedImg(
    imageSrc: string,
    pixelCrop: Area,
    targetWidth: number,
    targetHeight: number
): Promise<Blob> {
    const image = new Image();
    image.src = imageSrc;

    // Wait for image to load
    await new Promise((resolve) => {
        image.onload = resolve;
    });

    // Create canvas at target dimensions
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('Could not get canvas context');
    }

    // Draw the cropped portion of the image, scaled to target dimensions
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        targetWidth,
        targetHeight
    );

    // Convert canvas to blob
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Canvas toBlob failed'));
                }
            },
            'image/jpeg',
            0.9 // Quality
        );
    });
}

/**
 * ImageCropper component - allows users to crop an image to a specific aspect ratio.
 * Used for newspaper cover images (864x384 pixels, 2.25:1 aspect ratio).
 */
export function ImageCropper({
    imageSrc,
    onCropComplete,
    onCancel,
    targetWidth = 864,
    targetHeight = 384,
}: ImageCropperProps) {
    // Crop state
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Calculate aspect ratio from target dimensions
    const aspect = targetWidth / targetHeight;

    /**
     * Called when the crop area changes.
     */
    const onCropAreaComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    /**
     * Handle the confirm button - create cropped image and call callback.
     */
    const handleConfirm = async () => {
        if (!croppedAreaPixels) return;

        setIsProcessing(true);
        try {
            const croppedBlob = await getCroppedImg(
                imageSrc,
                croppedAreaPixels,
                targetWidth,
                targetHeight
            );
            onCropComplete(croppedBlob);
        } catch (error) {
            console.error('Error cropping image:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
            <div className="bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-700">
                    <h3 className="text-lg font-semibold text-white">Crop Cover Image</h3>
                    <p className="text-sm text-gray-400">
                        Adjust the crop area to select the portion of the image to use.
                        Final size: {targetWidth}×{targetHeight} pixels
                    </p>
                </div>

                {/* Cropper Area */}
                <div className="relative h-96 bg-slate-900">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropAreaComplete}
                    />
                </div>

                {/* Zoom Control */}
                <div className="px-6 py-3 border-t border-slate-700 bg-slate-800">
                    <label className="flex items-center gap-4 text-sm text-gray-300">
                        <span>Zoom:</span>
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="flex-1 h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="w-12 text-right">{zoom.toFixed(1)}×</span>
                    </label>
                </div>

                {/* Action Buttons */}
                <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isProcessing || !croppedAreaPixels}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                    >
                        {isProcessing ? 'Processing...' : 'Apply Crop'}
                    </button>
                </div>
            </div>
        </div>
    );
}

